// DonateModal.jsx

import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  IconButton,
  TextField,
  Button,
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";
import { createCheckoutSession } from "../../apis/user-apis/DonationApis";
import CircularProgress from "@mui/material/CircularProgress";

const NAME_REGEX = /^[\p{L}\p{M}\s'-’]+$/u;
const filterName = (value) => value.replace(/[^\p{L}\p{M}\s'-’]/gu, "");

const DonateModal = ({ open, handleClose, amount = 100, onSuccess }) => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    state: "",
    zipCode: "",
    country: "",
  });

  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  // HANDLE CHANGE
  const handleChange = (e) => {
    const { name, value } = e.target;

    // NAME VALIDATION (UNICODE LETTERS & SPACES)
    if (name === "firstName" || name === "lastName") {
      const filteredValue = filterName(value);

      setFormData((prev) => ({
        ...prev,
        [name]: filteredValue,
      }));

      return;
    }

    // PHONE VALIDATION (ONLY NUMBERS, MAX 10)
    if (name === "phone") {
      const filteredValue = value.replace(/\D/g, "").slice(0, 10);

      setFormData((prev) => ({
        ...prev,
        [name]: filteredValue,
      }));

      return;
    }

    // ZIP CODE VALIDATION (ONLY NUMBERS, MAX 5)
    if (name === "zipCode") {
      const filteredValue = value.replace(/\D/g, "").slice(0, 5);

      setFormData((prev) => ({
        ...prev,
        [name]: filteredValue,
      }));

      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // VALIDATE
  const validateForm = () => {
    const newErrors = {};

    // FIRST NAME
    if (!formData.firstName.trim()) {
      newErrors.firstName = "First name is required";
    } else if (!NAME_REGEX.test(formData.firstName.trim())) {
      newErrors.firstName = "First name should not contain special characters";
    }

    // LAST NAME
    if (!formData.lastName.trim()) {
      newErrors.lastName = "Last name is required";
    } else if (!NAME_REGEX.test(formData.lastName.trim())) {
      newErrors.lastName = "Last name should not contain special characters";
    }

    // EMAIL
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (
      !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formData.email)
    ) {
      newErrors.email = "Enter a valid email address";
    }

    // PHONE
    if (!formData.phone.trim()) {
      newErrors.phone = "Phone number is required";
    } else if (formData.phone.length !== 10) {
      newErrors.phone = "Phone number must be exactly 10 digits";
    }

    // ADDRESS
    if (!formData.address.trim()) {
      newErrors.address = "Address is required";
    }

    // CITY
    if (!formData.city.trim()) {
      newErrors.city = "City is required";
    }

    // STATE
    if (!formData.state.trim()) {
      newErrors.state = "State is required";
    }

    // ZIP CODE
    if (!formData.zipCode.trim()) {
      newErrors.zipCode = "ZIP Code is required";
    } else if (formData.zipCode.length < 5) {
      newErrors.zipCode = "Enter a valid 5-digit ZIP code";
    }

    // COUNTRY
    if (!formData.country.trim()) {
      newErrors.country = "Country is required";
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  // SUBMIT
  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsLoading(true);
    try {
      const result = await createCheckoutSession({
        ...formData,
        amount,
      });

      if (result.success && result.url) {
        // Save donor name and amount only after confirmed success, right before redirect
        localStorage.setItem("donorName", `${formData.firstName} ${formData.lastName}`.trim());
        localStorage.setItem("donorAmount", amount);
        // Redirect to Stripe Checkout
        window.location.href = result.url;
      } else {
        alert(result.error || "Failed to initiate payment");
      }
    } catch (error) {
      console.error("Donation Error:", error);
      alert(error.error || "An unexpected error occurred. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "10px",
          overflow: "hidden",
        },
      }}
    >
      {/* HEADER */}
      <Box
        sx={{
          bgcolor: "#081B44",
          px: { xs: 2, sm: 3 },
          py: { xs: 2, sm: 3 },
          position: "relative",
        }}
      >
        {/* CLOSE BUTTON */}
        <IconButton
          onClick={handleClose}
          sx={{
            position: "absolute",
            top: 18,
            right: 18,
            color: "#fff",
            border: "1px solid rgba(255,255,255,0.4)",
            width: 40,
            height: 40,
          }}
        >
          <CloseIcon />
        </IconButton>

        {/* CONTRIBUTION */}
        <Typography
          sx={{
            color: "#D4A017",
            fontSize: "11px",
            fontWeight: 700,
            letterSpacing: "2px",
            textTransform: "uppercase",
            mb: 1.5,
          }}
        >
          Contribution
        </Typography>

        {/* TITLE */}
        <Typography
          sx={{
            color: "#fff",
            fontSize: {
              xs: "20px",
              md: "24px",
            },
            fontWeight: 700,
            lineHeight: 1.2,
            fontFamily: "'Playfair Display', serif",
          }}
        >
          Donate ${amount} to the Campaign
        </Typography>
      </Box>

      {/* CONTENT */}
      <DialogContent
        sx={{
          px: { xs: 2, sm: 4 },
          py: { xs: 3, sm: 4 },
          bgcolor: "#fff",
        }}
      >
        {/* DONATION INFO */}
        <Box
          sx={{
            bgcolor: "#F7F4EE",
            borderRadius: "8px",
            px: 3,
            py: 3,
            display: "flex",
            justifyContent: "space-between",
            gap: 4,
            flexWrap: "wrap",
          }}
        >
          <Box>
            <Typography sx={smallHeadingStyle}>Amount</Typography>

            <Typography
              sx={{
                fontSize: "20px",
                fontWeight: 700,
                color: "#081B44",
              }}
            >
              ${amount}
            </Typography>
          </Box>

          <Box>
            <Typography sx={smallHeadingStyle}>Goes Toward</Typography>

            <Typography
              sx={{
                fontSize: "15px",
                color: "#555",
                lineHeight: 1.7,
              }}
            >
              🟨 ${amount} = one full canvassing event covering 50 homes
            </Typography>
          </Box>
        </Box>

        {/* DIVIDER */}
        <Box
          sx={{
            borderBottom: "1px solid #E6E6E6",
            my: 2,
          }}
        />

        {/* SECTION TITLE */}
        <Typography
          sx={{
            fontSize: "20px",
            fontWeight: 700,
            color: "#081B44",
            fontFamily: "'Playfair Display', serif",
            mb: 1,
          }}
        >
          Your Information
        </Typography>

        <Typography
          sx={{
            color: "#6B7280",
            fontSize: "16px",
            mb: 4,
          }}
        >
          We'll pre-fill this on the secure payment page so you don't have to
          re-enter it.
        </Typography>

        {/* FIRST + LAST NAME */}
        <Box
          sx={{
            display: "flex",
            gap: 2,
            mb: 3,
            flexWrap: "wrap",
          }}
        >
          <Box sx={{ flex: 1, minWidth: "200px" }}>
            <Typography sx={labelStyle}>FIRST NAME *</Typography>

            <TextField
              fullWidth
              size="small"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              error={Boolean(errors.firstName)}
              helperText={errors.firstName}
            />
          </Box>

          <Box sx={{ flex: 1, minWidth: "200px" }}>
            <Typography sx={labelStyle}>LAST NAME *</Typography>

            <TextField
              fullWidth
              size="small"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              error={Boolean(errors.lastName)}
              helperText={errors.lastName}
            />
          </Box>
        </Box>

        {/* EMAIL + PHONE */}
        <Box
          sx={{
            display: "flex",
            gap: 2,
            mb: 3,
            flexWrap: "wrap",
          }}
        >
          <Box sx={{ flex: 1, minWidth: "200px" }}>
            <Typography sx={labelStyle}>EMAIL *</Typography>

            <TextField
              fullWidth
              size="small"
              name="email"
              value={formData.email}
              onChange={handleChange}
              error={Boolean(errors.email)}
              helperText={errors.email}
            />
          </Box>

          <Box sx={{ flex: 1, minWidth: "200px" }}>
            <Typography sx={labelStyle}>PHONE *</Typography>

            <TextField
              fullWidth
              size="small"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder="9876543210"
              error={Boolean(errors.phone)}
              helperText={errors.phone}
            />
          </Box>
        </Box>

        {/* ADDRESS */}
        <Box sx={{ mb: 3 }}>
          <Typography sx={labelStyle}>ADDRESS *</Typography>

          <TextField
            fullWidth
            size="small"
            name="address"
            value={formData.address}
            onChange={handleChange}
            placeholder="Street Address"
            error={Boolean(errors.address)}
            helperText={errors.address}
          />
        </Box>

        {/* CITY + STATE + ZIP CODE */}
        <Box
          sx={{
            display: "flex",
            gap: 2,
            mb: 1,
            flexWrap: "wrap",
          }}
        >
          <Box sx={{ flex: 2, minWidth: { xs: "100%", sm: "240px" } }}>
            <Typography sx={labelStyle}>CITY *</Typography>

            <TextField
              fullWidth
              size="small"
              name="city"
              value={formData.city}
              onChange={handleChange}
              placeholder="Middletown"
              error={Boolean(errors.city)}
              helperText={errors.city}
            />
          </Box>

          <Box sx={{ flex: 1, minWidth: { xs: "100%", sm: "140px" } }}>
            <Typography sx={labelStyle}>STATE *</Typography>

            <TextField
              fullWidth
              size="small"
              name="state"
              value={formData.state}
              onChange={handleChange}
              placeholder="DE"
              error={Boolean(errors.state)}
              helperText={errors.state}
            />
          </Box>

          <Box sx={{ flex: 1, minWidth: { xs: "100%", sm: "140px" } }}>
            <Typography sx={labelStyle}>ZIP CODE *</Typography>

            <TextField
              fullWidth
              size="small"
              name="zipCode"
              value={formData.zipCode}
              onChange={handleChange}
              placeholder="19709"
              error={Boolean(errors.zipCode)}
              helperText={errors.zipCode}
            />
          </Box>

          <Box sx={{ flex: 1, minWidth: { xs: "100%", sm: "140px" } }}>
            <Typography sx={labelStyle}>COUNTRY *</Typography>

            <TextField
              fullWidth
              size="small"
              name="country"
              value={formData.country}
              onChange={handleChange}
              placeholder="US"
              error={Boolean(errors.country)}
              helperText={errors.country}
            />
          </Box>
        </Box>

        <Typography
          sx={{
            fontSize: "12px",
            color: "#6B7280",
            mb: 4,
          }}
        >
          Required by Delaware campaign finance law.
        </Typography>

        {/* BUTTON */}
        <Button
          fullWidth
          variant="contained"
          onClick={handleSubmit}
          disabled={isLoading}
          sx={{
            bgcolor: "#C99723",
            color: "#081B44",
            fontWeight: 700,
            fontSize: "18px",
            textTransform: "uppercase",
            borderRadius: "4px",
            py: 1.5,
            boxShadow: "none",
            mb: 3,
            "&:hover": {
              bgcolor: "#B8860B",
              boxShadow: "none",
            },
          }}
        >
          {isLoading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            `Continue to Donate $${amount}`
          )}
        </Button>

        {/* FOOTER */}
        <Box sx={{ textAlign: "center" }}>
          <Typography
            sx={{
              fontSize: "13px",
              color: "#6B7280",
              mb: 0.5,
            }}
          >
            🔒 Your information is kept private and used only for campaign
            finance reporting.
          </Typography>

          <Typography
            sx={{
              fontSize: "13px",
              color: "#6B7280",
            }}
          >
            Subject to Delaware campaign finance laws. Individual limit: $600
            /cycle. Not tax deductible.
          </Typography>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

const labelStyle = {
  fontSize: "13px",
  fontWeight: 700,
  color: "#081B44",
  mb: 1,
  letterSpacing: "0.5px",
};

const smallHeadingStyle = {
  fontSize: "11px",
  fontWeight: 700,
  color: "#D4A017",
  letterSpacing: "1px",
  textTransform: "uppercase",
  mb: 1,
};

export default DonateModal;
